import {Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from "@angular/core";

@Component({
    selector: 'app-diff-viewer',
    templateUrl: './diff-viewer.component.html',
    styleUrls: ['./diff-viewer.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DiffViewerComponent implements OnChanges {

    @Input()
    public diffGroupDefinitions: DiffGroupDefinition[];

    public diffGroups: DiffGroup[] = [];

    @Input()
    public showEqualValues: boolean = false;

    private compareArrays(oldArray: any[], newArray: any[], comparer: (a: any, b: any) => boolean) {
        const unchanged = newArray.filter(newItem => oldArray.some(oldItem => comparer(oldItem, newItem)));
        const added = newArray.filter(newItem => !oldArray.some(oldItem => comparer(oldItem, newItem)));
        const removed = oldArray.filter(oldItem => !newArray.some(newItem => comparer(oldItem, newItem)));
        return {added, removed, unchanged};
    }

    private formatArrayItems(items: any[], formatter: (item: any) => string): string {
        return items.length > 0 ? items.map(formatter).join(', ') : '-';
    }

    private generateDiffs(properties: DiffProperty[], oldObject: any, newObject: any) {
        const result: Diff[] = [];

        if (!oldObject && !newObject) {
            return result;
        }

        for (let property of properties) {
            if (property.arrayValue) {
                if (!property.itemComparer || !property.itemFormatter) {
                    throw new Error(`DiffProperty with arrayValue must provide both itemComparer and itemFormatter`);
                }

                const oldArray = oldObject ? (property.arrayValue(oldObject) ?? []) : [];
                const newArray = newObject ? (property.arrayValue(newObject) ?? []) : [];
                const {added, removed, unchanged} = this.compareArrays(oldArray, newArray, property.itemComparer);
                const formatter = property.itemFormatter;

                const oldValue = this.formatArrayItems([...unchanged, ...removed], formatter);
                const newValue = this.formatArrayItems([...unchanged, ...added], formatter) +
                    (added.length > 0 || removed.length > 0 ? '\n' : '') +
                    (added.length > 0 ? `\nHinzugefügt: ${this.formatArrayItems(added, formatter)}` : '') +
                    (removed.length > 0 ? `\nEntfernt: ${this.formatArrayItems(removed, formatter)}` : '');

                if (added.length > 0 || removed.length > 0 || this.showEqualValues) {
                    result.push({displayName: property.displayName, oldValue, newValue});
                }
            } else {
                if (!property.value) {
                    throw new Error(`DiffProperty which is not an array, must provide a value`);
                }

                let oldValue = oldObject ? property.value(oldObject) ?? '': '';
                let newValue = newObject ? property.value(newObject) ?? '': '';
                let areEqual = oldValue == newValue;
                if (areEqual && (!this.showEqualValues || oldValue == '')) continue;
                result.push({displayName: property.displayName, oldValue, newValue: areEqual && this.showEqualValues ? '- unverändert - ' : newValue});
            }
        }

        return result;
    }

    private generateDiffGroups(def: DiffGroupDefinition): DiffGroup[] {
        const diffGroups: DiffGroup[] = [];
        def.oldItems.forEach((oldRep, index) => {
            const newRep = def.newItems[index];
            const repDiffs = this.generateDiffs(def.properties, oldRep, newRep);
            if (repDiffs.length > 0) diffGroups.push({
                name: def.groupNameAddition
                    ? `${def.groupName} - ${def.groupNameAddition(oldRep)}`
                    : def.groupName,
                diffs: repDiffs
            });
        });

        return diffGroups;
    }

    ngOnChanges(_: SimpleChanges): void {
        if (!this.diffGroupDefinitions) {
            return;
        }

        this.diffGroups = [];
        this.diffGroupDefinitions.forEach(groupDef => this.diffGroups = this.diffGroups.concat(this.generateDiffGroups(groupDef)));
    }
}

export interface Diff {
    displayName: string;
    oldValue: string;
    newValue: string;
}

export interface DiffGroup {
    name: string;
    diffs: Diff[];
}

export interface DiffProperty {
    displayName: string;
    value?: (obj: any) => string;
    arrayValue?: (obj: any) => any[];
    itemFormatter?: (item: any) => string;
    itemComparer?: (a: any, b: any) => boolean;
}

export interface DiffGroupDefinition {
    groupName: string;
    properties: DiffProperty[];
    oldItems: any[];
    newItems: any[];
    groupNameAddition?: (obj: any) => string;
}
